// Temporary: forwards jQuery Migrate warnings to Sentry while the jQuery upgrade is under way.
// Removed together with Migrate (MEP 3): once no warning comes in anymore, Migrate can go.

/**
 * Migrate keeps its own history of the messages it logged: it is stable for a given Migrate version,
 * unlike console output, and it is filled even when `jQuery.migrateMute` silences the console.
 *
 * @param {object} options
 * @param {object} options.jQuery - the global jQuery, Migrate possibly loaded
 * @param {() => Storage} options.getStorage - may throw (private browsing, blocked storage)
 * @param {(message: string, context: object) => void} options.captureMessage - Sentry.captureMessage
 * @param {string} options.releaseVersion - window._release_version
 */
export function installJqueryMigrateSentryBridge({ jQuery, getStorage, captureMessage, releaseVersion }) {
  // Migrate 4 renamed the history
  const migrateHistory = jQuery.migrateMessages ?? jQuery.migrateWarnings;
  if (!Array.isArray(migrateHistory)) return;

  const jqueryVersion = jQuery.fn.jquery;

  function report(entry) {
    const dedupKey = `jquery-migrate:${releaseVersion}:${entry}`;

    // Once per release and browser, to spare the Sentry quota: pages log the same warnings on every load.
    // Storage may be unavailable: report anyway rather than lose the warning.
    let storage = null;
    try {
      storage = getStorage();
      if (storage.getItem(dedupKey)) return;
    } catch {
      // reported below
    }

    captureMessage(`JQMIGRATE: ${entry}`, {
      level: "warning",
      fingerprint: ["jquery-migrate", entry],
      tags: { jquery_migrate: jqueryVersion },
    });

    try {
      storage?.setItem(dedupKey, "1");
    } catch {
      // nothing to do
    }
  }

  // Reporting runs while app.js is evaluated and inside the user's jQuery calls: a Sentry failure
  // must neither stop the app nor break the jQuery call.
  function reportSafely(entry) {
    try {
      report(entry);
    } catch {
      // the warning is lost, the app keeps going
    }
  }

  migrateHistory.forEach(reportSafely);

  // Patch this instance only: Migrate's reset empties the array in place, so the instance is kept.
  // Non-enumerable, so the patch does not show up when the history is iterated or compared.
  const originalPush = migrateHistory.push;
  Object.defineProperty(migrateHistory, "push", {
    configurable: true,
    writable: true,
    value: function (...entries) {
      // History first: Migrate's own record must not depend on Sentry.
      const newLength = originalPush.apply(this, entries);
      entries.forEach(reportSafely);
      return newLength;
    },
  });
}
